import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export interface SubjectMarksMapping {
  /**
   * Backend uses `@GeneratedValue`, so `id` is optional for create.
   */
  id?: number;

  className: string;
  subjectCode: string;
  subjectName: string;
  subjectType: string;
  groupName: string;

  totalMarks: number;
  theoryMarks: number;
  practicalMarks: number;

  thPassMarks: number;
  prPassMarks: number;

  isAllowedGrace: boolean;
  grace: number;

  e1Th: number;
  e1Pr: number;
  e2Th: number;
  e2Pr: number;

  /**
   * Subject display priority in marksheet (lower comes first).
   * Backend may return `priority_no` (snake_case) or `priorityNo` (camelCase).
   */
  priority_no?: number;
  priorityNo?: number;
}

const API_URL = `${process.env.REACT_APP_API_URL}/subject-mark-mapping`;
const SUBJECT_MARKS_MAPPING_QUERY_KEY = "subject-marks-mapping" as const;

export type SubjectMarksMappingIdentifier = Pick<SubjectMarksMapping, "className" | "subjectCode">;

export const getBaseClassName = (className?: string): string => {
  if (!className) return "";
  const match = className.match(/^(\d+(?:th|st|nd|rd)?)/i);
  return match ? match[1] : className.split(/\s+/)[0];
};

const invalidateSubjectMarksMappings = (queryClient: ReturnType<typeof useQueryClient>, className?: string) => {
  const baseClassName = getBaseClassName(className);

  if (baseClassName) {
    void queryClient.invalidateQueries({
      queryKey: [SUBJECT_MARKS_MAPPING_QUERY_KEY, baseClassName],
    });
  }

  void queryClient.invalidateQueries({
    queryKey: [SUBJECT_MARKS_MAPPING_QUERY_KEY],
  });
};

const addSubjectMarkMapping = async (payload: SubjectMarksMapping) => {
  const response = await axios.post(API_URL, payload);
  return response.data as SubjectMarksMapping;
};

const putSubjectMarkMapping = async (payload: SubjectMarksMapping) => {
  const response = await axios.put(API_URL, payload);
  return response.data as SubjectMarksMapping;
};

const deleteSubjectMarkMapping = async (payload: SubjectMarksMappingIdentifier) => {
  // Backend expects the identifier in request body.
  await axios.delete(API_URL, { data: payload });
};

/** GET `/{className}` on subject-mark-mapping controller */
export const getSubjectMarksMappingByClassName = async (
  className: string
): Promise<SubjectMarksMapping[]> => {
  const response = await axios.get(`${API_URL}/${encodeURIComponent(className)}`);
  return response.data;
};

export function useSubjectMarksMappingByClassName(
  className: string | undefined,
  tenantLoading: boolean = false
) {
  return useQuery<SubjectMarksMapping[]>({
    queryKey: [SUBJECT_MARKS_MAPPING_QUERY_KEY, className],
    queryFn: () => getSubjectMarksMappingByClassName(className!),
    enabled: !!className && !tenantLoading,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
}

export function useSubjectMarkMapping() {
  const queryClient = useQueryClient();

  const addSubjectMarkMappingMutation = useMutation({
    mutationFn: addSubjectMarkMapping,
    onSuccess: (_data, variables) => {
      invalidateSubjectMarksMappings(queryClient, variables?.className);
    },
  });

  const deleteSubjectMarkMappingMutation = useMutation({
    mutationFn: deleteSubjectMarkMapping,
    onSuccess: (_data, variables) => {
      invalidateSubjectMarksMappings(queryClient, variables?.className);
    },
  });

  const updateSubjectMarkMappingMutation = useMutation({
    mutationFn: putSubjectMarkMapping,
    onSuccess: (_data, variables) => {
      invalidateSubjectMarksMappings(queryClient, variables?.className);
    },
  });

  return {
    addSubjectMarkMappingMutation,
    deleteSubjectMarkMappingMutation,
    updateSubjectMarkMappingMutation,
  };
}

